import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString } from "vue"

import { ref, watch, computed } from 'vue'
import useParamCode from '@/hooks/useParamCode'
import { useInteractionStore } from '@/stores/interaction'
import type { ParamCodeItem } from '@/typings/common'
import { useAreaStore } from '@/stores/area'


const __default__ = { name: 'BusinessName' }

interface ParamCodeData {
  fbu_head_domestic_warehouse?: ParamCodeItem[]
  fbu_head_destination_country_warehouse?: ParamCodeItem[]
  fbu_head_replenish_method?: ParamCodeItem[]
  fbu_tail_warehouse_name?: ParamCodeItem[]
  ofbu_head_domestic_warehouse?: ParamCodeItem[]
  ofbu_head_destination_country_warehouse?: ParamCodeItem[]
  ofbu_head_replenish_method?: ParamCodeItem[]
  ofbu_tail_warehouse_name?: ParamCodeItem[]
  lbu_demand_country?: ParamCodeItem[]
  lbu_products?: ParamCodeItem[]
}


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  value: {
    type: String
  },
  label: {
    type: String
  },
  appId: {
    type: String,
    required: true,
    validator: (value: string) => {
      return ['cos_fbu', 'cos_ofbu', 'cos_lbu', 'cos_zbu'].includes(value)
    }
  }
},
  emits: ['update:label'],
  setup(__props, { emit: __emit }) {

const areaStore = useAreaStore()
const { getParamCodes } = useParamCode()
const interactionStore = useInteractionStore()
const props = __props

const paramCodeData = ref<ParamCodeData>({
  fbu_head_domestic_warehouse: [], // 头程-国内仓库
  fbu_head_destination_country_warehouse: [], // 头程-目的国仓库
  fbu_head_replenish_method: [], // 头程-运输方式
  fbu_tail_warehouse_name: [], // 尾程-仓库名称
  ofbu_head_domestic_warehouse: [], // 头程-国内仓库
  ofbu_head_destination_country_warehouse: [], // 头程-目的国仓库
  ofbu_head_replenish_method: [], // 头程-运输方式
  ofbu_tail_warehouse_name: [], // 尾程-仓库名称
  lbu_demand_country: [], // 需求国家
  lbu_products: [] // 云途产品
})

const businessName = ref<string>()
const language = computed(() => interactionStore.language)
const nameKey = computed(() => {
  const keyMap = { 'en-US': 'enName', 'zh-CN': 'cnName' }
  return keyMap[language.value] || 'name'
})

const formatFbu = (paramCodeData: ParamCodeData, businessType: 'fbu' | 'ofbu') => {
  const result = []
  const list = props.value?.split(',').filter((item: string) => item)
  const tailWarehouseNameKey = `${businessType}_tail_warehouse_name`
  const headDomesticWarehouseKey = `${businessType}_head_domestic_warehouse`
  const headDestinationCountryWarehouseKey = `${businessType}_head_destination_country_warehouse`
  const headReplenishMethodKey = `${businessType}_head_replenish_method`

  if (businessType === 'ofbu' && list && list.length === 1) {
    // 尾程，只有仓库名称（ofbu）
    const [warehouseName] = list
    const warehouse = paramCodeData?.[tailWarehouseNameKey]?.find(
      (item: ParamCodeItem) => item.id === parseInt(warehouseName)
    )
    result.push(warehouse?.[nameKey.value] || warehouseName)
    businessName.value = result.join('+')
  } else if (list && list.length === 2) {
    // 尾程
    const [warehouseName, productName] = list
    const warehouse = paramCodeData?.[tailWarehouseNameKey]?.find(
      (item: ParamCodeItem) => item.id === parseInt(warehouseName)
    )
    const product = warehouse?.children?.find((item: ParamCodeItem) => item.id === parseInt(productName))
    result.push(warehouse?.[nameKey.value] || warehouseName)
    result.push(product?.[nameKey.value] || productName)
    businessName.value = result.join('+')
  } else if (list && list.length === 3) {
    // 头程
    const [domesticWarehouse, destinationCountryWarehouse, replenishMethod] = list
    const domesticWarehouseName = paramCodeData?.[headDomesticWarehouseKey]?.find(
      (item: ParamCodeItem) => item.id === parseInt(domesticWarehouse)
    )
    const destinationCountryWarehouseName = paramCodeData?.[headDestinationCountryWarehouseKey]?.find(
      (item: ParamCodeItem) => item.id === parseInt(destinationCountryWarehouse)
    )
    const replenishMethodName = paramCodeData?.[headReplenishMethodKey]?.find(
      (item: ParamCodeItem) => item.id === parseInt(replenishMethod)
    )
    result.push(domesticWarehouseName?.[nameKey.value] || domesticWarehouse)
    result.push(destinationCountryWarehouseName?.[nameKey.value] || destinationCountryWarehouse)
    result.push(replenishMethodName?.[nameKey.value] || replenishMethod)
    businessName.value = result.join('+')
  } else {
    businessName.value = props.value
  }
}
const formatLbu = (paramCodeData: ParamCodeData) => {
  const result = []
  const list = props.value?.split(',')
  if (list && list.length) {
    const [originCountry, country, channel] = list
    const originCountryName = areaStore.area.find((item) => item.id === originCountry)?.name;
    result.push(originCountryName || originCountry)
    const countryName = paramCodeData?.lbu_demand_country?.find((item: ParamCodeItem) => item.id === parseInt(country))
    const flatList = flatTreeAndSetLevel(paramCodeData?.lbu_products)
    // const channelName = getParentName(parseInt(channel), flatList)
    // 只找最后一级
    const channelName = flatList.find((item) => item.id === parseInt(channel))
    result.push(countryName?.name || country)
    result.push(channelName?.name || channel)
    businessName.value = result.join('+')
  } else {
    businessName.value = props.value
  }
}

// 获取业务参数
const getByParamCodes = () => {
  const localData = JSON.parse(localStorage.getItem(`BusinessNameData_${props.appId}`) || '{}')
  const currentTime = new Date().getTime()
  const isEffective = currentTime - localData.date < 1000 * 60 * 10 // 设置缓存有效期 10 分钟
  if (JSON.stringify(localData) !== '{}' && isEffective) {
    // 从缓存获取数据
    paramCodeData.value = localData.data
    if (props.appId === 'cos_fbu') {
      formatFbu(paramCodeData.value, 'fbu')
    } else if (props.appId === 'cos_ofbu') {
      formatFbu(paramCodeData.value, 'ofbu')
    } else if (props.appId === 'cos_lbu') {
      formatLbu(paramCodeData.value)
    }
  } else {
    // 从接口获取数据
    const codes = {
      cos_fbu: [
        'fbu_head_domestic_warehouse',
        'fbu_head_destination_country_warehouse',
        'fbu_head_replenish_method',
        'fbu_tail_warehouse_name'
      ],
      cos_ofbu: [
        'ofbu_head_domestic_warehouse',
        'ofbu_head_destination_country_warehouse',
        'ofbu_head_replenish_method',
        'ofbu_tail_warehouse_name'
      ],
      cos_lbu: ['lbu_demand_country', 'lbu_products']
    }
    getParamCodes(props.appId as 'cos_fbu' | 'cos_ofbu' | 'cos_lbu' | 'cos_zbu', codes[props.appId]).then((res) => {
      paramCodeData.value = res
      localStorage.setItem(`BusinessNameData_${props.appId}`, JSON.stringify({ date: new Date().getTime(), data: res }))
      if (props.appId === 'cos_fbu') {
        formatFbu(paramCodeData.value, 'fbu')
      } else if (props.appId === 'cos_ofbu') {
        formatFbu(paramCodeData.value, 'ofbu')
      } else if (props.appId === 'cos_lbu') {
        formatLbu(paramCodeData.value)
      }
    })
  }
}

const emit = __emit
watch(
  businessName,
  (val?: string) => {
    emit('update:label', val)
  },
  { immediate: true }
)
watch(
  () => language.value,
  () => {
    // 目前只有 cos_ofbu 支付多语言，所以只刷新这个
    if (props.appId === 'cos_ofbu') {
      formatFbu(paramCodeData.value, 'ofbu')
    }
  }
)

// 1. 先将树形结构扁平化，处理为同级
const flatTreeAndSetLevel = (tree: any, level = 1) => {
  const list: any[] = []
  if (tree !== undefined) {
    tree.forEach((item: any) => {
      if (level === 1) item.parentId = 0 // 第一级设置父级id为0
      const o = JSON.parse(JSON.stringify(item))
      if (o.children) delete o.children
      o.level = level
      list.push(o)
      if (item.children && item.children.length) {
        list.push(...flatTreeAndSetLevel(item.children, level + 1))
      }
    })
  }
  return list
}
// 2. 遍历上面扁平后的的数据，先根据id找出第一个，然后再根据它的parentId再找，直到找到parentId为0的为止，也是一个递归操作
const getParentName: any = (pid: number, list: any) => {
  let obj: any = null
  obj = list.find((item: any) => item.id === pid)
  if (obj && obj.parentId !== 0) return getParentName(obj.parentId, list)
  return obj
}

watch(
  () => props.value,
  async (val) => {
    if (val) {
      await areaStore.fetchData()
      getByParamCodes()
    }
  },
  { immediate: true }
)

return (_ctx: any,_cache: any) => {
  return _toDisplayString(businessName.value || '--')
}
}

})